import React, { useState } from 'react';
import apiClient from '../api/ApiClient';

// Simple Packages page for downloading CTRAC package.
// We use native HTML5 date inputs for a modern lightweight date-picker UI
// to avoid adding new dependencies. The ApiClient.downloadCtracPackage
// method is used to request the zip and trigger a browser download.

export const Packages = () => {
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [includeRealtimeCandles, setIncludeRealtimeCandles] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [filename, setFilename] = useState(null);

	// CSV download state
	const [csvLoading, setCsvLoading] = useState(false);
	const [csvError, setCsvError] = useState(null);
	const [csvFilename, setCsvFilename] = useState(null);

	// EXE download state
	const [exeLoading, setExeLoading] = useState(false);
	const [exeError, setExeError] = useState(null);
	const [exeFilename, setExeFilename] = useState(null);

	// Read package state
	const [readLoading, setReadLoading] = useState(false);
	const [readError, setReadError] = useState(null);
	const [packageContents, setPackageContents] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);

	const onDownload = async (e) => {
		e.preventDefault();
		setError(null);
		setFilename(null);
		setLoading(true);
		try {
		const payload = {
			startDate: startDate || null,
			endDate: endDate || null,
			includeRealtimeCandles,
		};
		const { url, filename } = await apiClient.fetchPackage(payload, "ctrac");			if (url && filename) {
				const a = document.createElement('a');
				a.href = url;
				a.download = filename;
				document.body.appendChild(a);
				a.click();
				a.remove();
				window.URL.revokeObjectURL(url);
			}

			setFilename(filename || 'ctrac_package.zip');
		} catch (err) {
			setError(err.message || String(err));
		} finally {
			setLoading(false);
		}
	};

	const onDownloadCsv = async (e) => {
		e.preventDefault();
		setCsvError(null);
		setCsvFilename(null);
		setCsvLoading(true);
		try {
		const payload = {
			startDate: startDate || null,
			endDate: endDate || null,
			includeRealtimeCandles,
		};
		const { url, filename } = await apiClient.fetchPackage(payload, "csv");			if (url && filename) {
				const a = document.createElement('a');
				a.href = url;
				a.download = filename;
				document.body.appendChild(a);
				a.click();
				a.remove();
				window.URL.revokeObjectURL(url);
			}

			setCsvFilename(filename || 'csv_package.zip');
		} catch (err) {
			setCsvError(err.message || String(err));
		} finally {
			setCsvLoading(false);
		}
	};

	const onDownloadExe = async (e) => {
		e.preventDefault();
		setExeError(null);
		setExeFilename(null);
		setExeLoading(true);
		try {
			const payload = {
				startDate: startDate || null,
				endDate: endDate || null,
				includeRealtimeCandles,
			};
			const { url, filename } = await apiClient.fetchPackage(payload, "ctrac-exe");
			if (url && filename) {
				const a = document.createElement('a');
				a.href = url;
				a.download = filename;
				document.body.appendChild(a);
				a.click();
				a.remove();
				window.URL.revokeObjectURL(url);
			}
			setExeFilename(filename || 'ctrac_package.exe');
		} catch (err) {
			setExeError(err.message || String(err));
		} finally {
			setExeLoading(false);
		}
	};

	const onFileSelect = (e) => {
		const file = e.target.files?.[0];
		setSelectedFile(file || null);
		setPackageContents(null);
		setReadError(null);
	};

	const onReadPackage = async (e) => {
		e.preventDefault();
		if (!selectedFile) {
			setReadError('Please select a file');
			return;
		}

		setReadError(null);
		setPackageContents(null);
		setReadLoading(true);

		try {
			const data = await apiClient.readPackage(selectedFile);
			setPackageContents(data);
		} catch (err) {
			setReadError(err.message || String(err));
		} finally {
			setReadLoading(false);
		}
	};

	const formatDate = (dateStr) => {
		try {
			const date = new Date(dateStr);
			return date.toLocaleDateString();
		} catch {
			return dateStr;
		}
	};

	return (
		<div style={{ maxWidth: 900 }}>
			<h2>Generate packages</h2>
			<form onSubmit={onDownload} className="mb-3" style={{ display: 'grid', gap: '0.75rem' }}>
				<div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
					<label style={{ minWidth: 90 }} className="form-label">Start date</label>
					<input
						type="date"
						className="form-control"
						style={{ maxWidth: 200 }}
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						aria-label="Start date"
					/>
					<label style={{ minWidth: 60, marginLeft: 12 }} className="form-label">End date</label>
					<input
						type="date"
						className="form-control"
						style={{ maxWidth: 200 }}
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						aria-label="End date"
					/>
				</div>

				<div className="form-check">
					<input
						type="checkbox"
						className="form-check-input"
						id="includeRealtimeCandles"
						checked={includeRealtimeCandles}
						onChange={(e) => setIncludeRealtimeCandles(e.target.checked)}
				/>
					<label htmlFor="includeRealtimeCandles" className="form-check-label">
						Include real-time candles
						<span style={{ color: '#666', fontSize: '0.85em', marginLeft: '0.4em' }}>
							(collects current-day quotes before generating)
						</span>
					</label>
				</div>

				<div className="d-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
					<button type="submit" className="btn btn-primary" disabled={loading}>
						{loading ? 'Preparing...' : 'Download CTRAC Package'}
					</button>
					<button
						type="button"
						className="btn btn-secondary"
						disabled={csvLoading}
						onClick={onDownloadCsv}
					>
						{csvLoading ? 'Preparing...' : 'Download CSV Package'}
					</button>
					<button
						type="button"
						className="btn btn-secondary"
						disabled={exeLoading}
						onClick={onDownloadExe}
					>
						{exeLoading ? 'Preparing...' : 'Download CTRAC package (exe)'}
					</button>
				</div>

				{error && (
					<div className="alert alert-danger" role="alert">Error: {error}</div>
				)}

				{filename && (
					<div className="alert alert-success">Downloaded: {filename}</div>
				)}

				{csvError && (
					<div className="alert alert-danger" role="alert">CSV Error: {csvError}</div>
				)}

				{csvFilename && (
					<div className="alert alert-success">CSV Downloaded: {csvFilename}</div>
				)}

				{exeError && (
					<div className="alert alert-danger" role="alert">EXE Error: {exeError}</div>
				)}

				{exeFilename && (
					<div className="alert alert-success">EXE Downloaded: {exeFilename}</div>
				)}
			</form>

			<hr style={{ margin: '2rem 0' }} />

			<h2>Read package</h2>
			<form onSubmit={onReadPackage} className="mb-3" style={{ display: 'grid', gap: '0.75rem' }}>
				<div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
					<label style={{ minWidth: 90 }} className="form-label">Package file</label>
					<input
						type="file"
						className="form-control"
						accept=".zip"
						onChange={onFileSelect}
						aria-label="Package file"
					/>
				</div>

				<div>
					<button
						type="submit"
						className="btn btn-primary"
						disabled={readLoading || !selectedFile}
					>
						{readLoading ? 'Reading...' : 'Read Package'}
					</button>
				</div>

				{readError && (
					<div className="alert alert-danger" role="alert">Error: {readError}</div>
				)}

				{packageContents && (
					<div style={{ marginTop: '1rem' }}>
						<h3>Package Contents</h3>
						{packageContents.Folders && packageContents.Folders.length > 0 ? (
							packageContents.Folders.map((folder, folderIdx) => (
								<div key={folderIdx} className="card" style={{ marginBottom: '1.5rem' }}>
									<div className="card-header"><h4 className="mb-0">Folder: {folder.FolderPath}</h4></div>
									<div className="card-body p-0">
									{folder.Securities && folder.Securities.length > 0 ? (
										<table className="table table-striped table-sm mb-0">
											<thead>
												<tr>
													<th>File#</th>
													<th>Name</th>
													<th>Symbol</th>
													<th>Fields</th>
													<th>First Date</th>
													<th>Last Date</th>
													<th>Periodicity</th>
												</tr>
											</thead>
											<tbody>
												{folder.Securities.map((sec, secIdx) => (
													<tr key={secIdx}>
														<td>{sec.FileNumber}</td>
														<td>{sec.Name}</td>
														<td>{sec.Symbol}</td>
														<td>{sec.NumFields}</td>
														<td>{formatDate(sec.FirstDate)}</td>
														<td>{formatDate(sec.LastDate)}</td>
														<td>{sec.Periodicity}</td>
													</tr>
												))}
											</tbody>
										</table>
									) : (
										<p className="text-muted m-3">No securities found in this folder</p>
									)}
									</div>
								</div>
							))
						) : (
							<p className="text-muted">No MASTER files found in the package</p>
						)}
					</div>
				)}
			</form>
		</div>
	);
}

export default Packages;

